import { Button, FormControl, InputLabel, MenuItem, Select, TextField, } from "@mui/material";
import axios from "axios";
import { forwardRef, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import Input from 'react-phone-number-input/input';
import { authData } from "../../components/GetAuth";
import { usePlacesWidget } from "react-google-autocomplete";
import styles from "./list.module.css";

const CustomInput = forwardRef((props, ref) => (<TextField    {...props} inputRef={ref} id="outlined-basic" label="Phone*" variant="outlined" />));

const AddCustomerForm = ({ onSubmitAddForm, onSubmitEditForm, setFormOpen, setSnackbarOpen, setSnackbarMessage, setSnackbarType, setLoading, editData }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [isRegCompany, setIsRegCompany] = useState("");
  const [selectPCountry, setSelectPCountry] = useState('GB');

  useEffect(() => {
    if (auth?.company && auth?.company?.pickup_country) {
      setSelectPCountry(auth?.company?.pickup_country);
    }
  }, [auth?.company]);

  const { register, handleSubmit, control, setValue, setError, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      post_code: "",
      registered_company: "",
      company_shipping: "",
      company_name: "",
    },
  });

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("address", place.formatted_address);

      setValue("latitude", place.geometry.location.lat());
      setValue("longitude", place.geometry.location.lng());

      place.address_components.forEach((item) => {
        if (item.types.indexOf("postal_code") > -1) {
          setValue("post_code", item.long_name);
        }
      });
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: selectPCountry },
    },
  });

  useEffect(() => {
    if (editData) {
      setValue("name", editData?.name);
      setValue("email", editData?.email);
      setValue("phone", editData?.phone);
      setValue("post_code", editData?.post_code);
      setValue("address", editData?.address);
      setValue("registered_company", editData?.registered_company);
      if (editData.registered_company === "Yes") {
        setValue("company_shipping", editData?.company_shipping);
        setValue("company_name", editData?.company_name);
        setIsRegCompany("Yes");
      }
    }
  }, [editData, setValue]);

  const onSubmitLocal = (data) => {
    setLoading(true);

    if (editData) {
      axios.post(process.env.REACT_APP_API_URL + "v1/customers/" + editData?.id, data, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setFormOpen(false);
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");

          onSubmitEditForm(response.data.data);
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
        if (response.data.status === "VALIDATIONERROR") {
          const serverErrors = response.data.errors;

          // Loop backend validation errors
          Object.keys(serverErrors).forEach((field) => {
            setError(field, {
              type: "server",
              message: serverErrors[field][0], // Laravel style
            });
          });;
        }
      }).catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
    } else {
      axios.post(process.env.REACT_APP_API_URL + "v1/customers", data, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setFormOpen(false);
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");

          onSubmitAddForm(response.data.data);
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
        if (response.data.status === "VALIDATIONERROR") {
          const serverErrors = response.data.errors;

          // Loop backend validation errors
          Object.keys(serverErrors).forEach((field) => {
            setError(field, {
              type: "server",
              message: serverErrors[field][0], // Laravel style
            });
          });;
        }
      }).catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
    }
  };

  return (<form onSubmit={handleSubmit(onSubmitLocal)}>
    <div className={`${styles.FormMainRow}`}>
      <div className={`${styles.FormGroupFull} FormGroup`}>
        <Controller
          control={control}
          {...register("name", { required: "This field is Required" })}
          render={({ field }) => (<TextField {...field} label="Full Name*" variant="outlined" />)}
        />
        {errors.name && (<span className={`ErrorM`}>{errors?.name?.message}</span>)}
      </div>
      <div className={`${styles.FormGroupFull} FormGroup`}>
        <Controller
          control={control}
          {...register("email", {
            required: "This field is Required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
          render={({ field }) => (<TextField {...field} label="Email*" variant="outlined" disabled={editData} />)}
        />
        {errors.email && (<span className={`ErrorM`}>{errors?.email?.message}</span>)}
      </div>
      <div className={`${styles.FormGroupFull} FormGroup`}>
        <Controller
          control={control}
          {...register("phone", {
            required: "This field is Required",
            validate: isValidPhoneNumber,
          })}
          render={({ field }) => (<Input {...field} country={selectPCountry} international withCountryCallingCode inputComponent={CustomInput} />)}
        />
        {errors.phone && errors.phone.type !== "validate" && (<span className={`ErrorM`}>{errors.phone.message}</span>)}
        {errors.phone && errors.phone.type === "validate" && (<span className={`ErrorM`}>Please enter valid phone</span>)}
      </div>
      <div className={`${styles.FormGroupFull} FormGroup`}>
        <Controller
          control={control}
          {...register("address", { required: "This field is Required" })}
          render={({ field }) => (<TextField {...field} label="Address*" variant="outlined" inputRef={ref} placeholder="" />)}
        />
        {errors.address && (<span className={`ErrorM`}>{errors?.address?.message}</span>)}
      </div>
      <div className={`${styles.FormGroupFull} FormGroup`}>
        <Controller
          control={control}
          {...register("post_code", { required: "This field is Required" })}
          render={({ field }) => (<TextField {...field} label="Post Code*" variant="outlined" />)}
        />
        {errors.post_code && (<span className={`ErrorM`}>{errors?.post_code?.message}</span>)}
      </div>
      <div className={`${styles.FormGroupFull} FormGroup`}>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Do you have a Registered Company?*</InputLabel>
          <Controller
            control={control}
            {...register("registered_company", {
              required: "This field is Required",
            })}
            render={({ field }) => (<Select
              {...field}
              label="Do you have a Registered Company?*"
              onChange={(e) => {
                field.onChange(e);
                setIsRegCompany(e.target.value);
              }}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>)}
          />
        </FormControl>
        {errors.registered_company && (<span className={`ErrorM`}>{errors?.registered_company?.message}</span>)}
      </div>
      {isRegCompany === "Yes" && (<div className={`${styles.FormGroupFull} FormGroup`}>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">If Yes, are you shipping the goods in the name of the company?*</InputLabel>
          <Controller
            control={control}
            {...register("company_shipping", {
              required: "This field is Required",
            })}
            render={({ field }) => (<Select {...field} label="If Yes, are you shipping the goods in the name of the company?*">
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>)}
          />
        </FormControl>
        {errors.company_shipping && (<span className={`ErrorM`}>{errors?.company_shipping?.message}</span>)}
      </div>)}
      {isRegCompany === "Yes" && (<div className={`${styles.FormGroupFull} FormGroup`}>
        <Controller
          control={control}
          {...register("company_name", {
            required: "This field is Required",
          })}
          render={({ field }) => (<TextField {...field} label="Registered Company Name*" variant="outlined" />)}
        />
        {errors.company_name && (<span className={`ErrorM`}>{errors?.company_name?.message}</span>)}
      </div>
      )}
    </div>

    <div className="ButtonAction">
      <Button className="CancelPopupBU" onClick={(e) => setFormOpen(false)}>Cancel</Button>
      <Button className="SubmitPopupBU" type="submit">{editData ? "Update" : "Add"}</Button>
    </div>
  </form>);
};

export default AddCustomerForm;